import { useEffect, useState } from "react";
import { Field, Form, Formik } from "formik";
import { useTranslation } from "react-i18next";
import { Tooltip } from "react-tooltip";
import classNames from "classnames";
import style from "./EventAuditList.module.css";
import { format } from "date-fns";

import { useAppSelector } from "../../shared/hook";
import Pagination from "../../shared/components/Table/Pagination";
import { EmptyListPlaceholderContent } from "../../shared/Ui/EmptyPageBlock";
import Calendar from "../../shared/Ui/Calendar/Calendar";
import { exportAuditEventsList, getAuditEventsList } from "../../common/api/auditApi";
import SelectCustomFormik from "../../shared/Ui/SelectCustom/SelectCustomFormik";

type PaginationState = {
	pageIndex: number;
	pageSize: number;
};

function EventsAuditList() {
	const { t } = useTranslation();
	const pageSize = useAppSelector((state) => state.userSettings.pageSize);

	const eventTypeOptions = [
		{ value: "REGISTRATION", label: t("auditEventType.registration") },
		{ value: "LOGIN", label: t("auditEventType.login") },
		{ value: "LOGOUT", label: t("auditEventType.logout") },
		{ value: "CREATE", label: t("auditEventType.create") },
		{ value: "SIGN", label: t("auditEventType.sign") },
		{ value: "UPDATE", label: t("auditEventType.update") },
		{ value: "DELETE", label: t("auditEventType.delete") },
		{ value: "EXPORT", label: t("auditEventType.export") },
	];

	const actionOptions = [
		{ value: "USER_REGISTRATION", label: "Регистрация пользователя" },
		{ value: "USER_LOGIN", label: "Авторизация пользователя" },
		{ value: "USER_LOGOUT", label: "Выход из системы" },
		{ value: "USER_UPDATE", label: "Редактирование данных пользователя" },
		{ value: "PRODUCT_GROUP_ACTIVATION", label: "Активация Товарной группы" },
		{ value: "PRODUCT_GROUP_DEACTIVATION", label: "Деактивация Товарной группы" },
		{ value: "CM_ORDER_CREATE_BY_GENERATION", label: "Создание заказа КМ, методом генерации марок" },
		{ value: "CM_ORDER_CREATE_BY_FILE", label: "Создание заказа КМ, методом загрузки файла" },
		{ value: "CM_ORDER_SIGN", label: "Подписание заказа КМ" },
		{ value: "CM_EXPORT", label: "Выгрузка КМ" },
		{ value: "CM_ORDER_UPDATE", label: "Редактирование заказа КМ" },
		{ value: "CM_ORDER_DELETE", label: "Удаление заказа КМ" },
		{ value: "DOCUMENT_CREATE", label: "Создание документа" },
		{ value: "DOCUMENT_SIGN", label: "Подписание документа" },
		{ value: "DOCUMENT_UPDATE", label: "Редактирование документа" },
		{ value: "DOCUMENT_DELETE", label: "Удаление документа" },
		{ value: "DOCUMENT_CM_EXPORT", label: "Выгрузка КМ из документа" },
		{ value: "DOCUMENT_PRINT", label: "Печать документа" },
		{ value: "DOCUMENT_REVOKE", label: "Отозвать документ" },
		{ value: "DOCUMENT_ACCEPT", label: "Принять документ" },
		{ value: "DOCUMENT_REJECT", label: "Отклонить документ" },
		{ value: "GAS_BALLOON_CREATE", label: "Создание карточки ГБ" },
		{ value: "GAS_BALLOON_UPDATE", label: "Редактирование карточки ГБ" },
		{ value: "GAS_BALLOON_DELETE", label: "Удаление карточки ГБ" },
		{ value: "BILLING_ACCOUNT_WRITE_OFF", label: "Списание с лицевого счета" },
	];

	const getEventTypeLabel = (value?: string) => {
		if (!value) return "-";
		return eventTypeOptions.find((option) => option.value === value)?.label ?? value;
	};

	const getActionLabel = (value?: string) => {
		if (!value) return "-";
		return actionOptions.find((option) => option.value === value)?.label ?? value;
	};

	const formatCreatedAt = (value?: string) => {
		if (!value) return "-";
		const date = new Date(value);
		if (isNaN(date.getTime())) return value;
		return format(date, "dd.MM.yyyy HH:mm:ss");
	};

	const [pagination, setPagination] = useState<PaginationState>({
		pageIndex: 0,
		pageSize: pageSize,
	});
	const [sort, setSort] = useState<"ASC" | "DESC">("DESC");
	const [tableData, setTableData] = useState<{
		currentPage?: number;
		itemsPerPage?: number;
		totalItems: number;
		totalPages: number;
	}>();

	const [values, setValues] = useState<any[]>([]);
	const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
	const [startDate, setStartDate] = useState<Date | undefined>(undefined);
	const [endDate, setEndDate] = useState<Date | undefined>(undefined);
	const [initialValues, setInitialValues] = useState<{
		userFio: string;
		orgIinBin: string;
		objectId: string;
		eventType: string[];
		description: string[];
		clientApp: string | null;
		createdAtFrom: string;
		createdAtTo: string;
	}>({
		userFio: "",
		orgIinBin: "",
		objectId: "",
		eventType: [],
		description: [],
		clientApp: null,
		createdAtFrom: "",
		createdAtTo: "",
	});

	const calculateItemNumber = (index: number) => {
		return pagination.pageIndex * pagination.pageSize + index + 1;
	};

	const loadList = async () => {
		try {
			const response = await getAuditEventsList(
				pagination.pageIndex + 1,
				pagination.pageSize,
				sort,
				initialValues,
			);

			setValues(response?.auditEvents || response?.items || response?.content || []);
			setTableData({
				currentPage: response?.currentPage ?? pagination.pageIndex + 1,
				itemsPerPage: response?.itemsPerPage ?? pagination.pageSize,
				totalItems: response?.totalItems ?? 0,
				totalPages: response?.totalPages ?? 0,
			});
		} catch (error) {
			console.error("Error fetching audit events:", error);
			setValues([]);
			setTableData({
				currentPage: pagination.pageIndex + 1,
				itemsPerPage: pagination.pageSize,
				totalItems: 0,
				totalPages: 0,
			});
		}
	};

	useEffect(() => {
		loadList();
	}, [pagination, sort]);

	useEffect(() => {
		setInitialValues((prev) => ({
			...prev,
			createdAtFrom: startDate ? format(startDate, "dd.MM.yyyy") : "",
			createdAtTo: endDate ? format(endDate, "dd.MM.yyyy") : "",
		}));
	}, [startDate, endDate]);

	const toggleSort = () => {
		const newSortDirection = sort === "ASC" ? "DESC" : "ASC";
		setSort(newSortDirection);
	};

	const handleDownload = async () => {
		try {
			const response = await exportAuditEventsList(sort, initialValues);

			if (!response) {
				console.error("Не удалось получить данные для скачивания.");
				return;
			}

			const now = new Date();
			const formattedDate = now.toLocaleDateString("ru-RU");
			const formattedTime = now.toLocaleTimeString("ru-RU").replace(/:/g, "-");
			const fileName = `Мониторинг действий пользователей (${formattedDate} ${formattedTime}).csv`;

			const fileBlob = new Blob([response], {
				type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
			});
			const downloadUrl = window.URL.createObjectURL(fileBlob);
			const link = document.createElement("a");
			link.href = downloadUrl;
			link.setAttribute("download", fileName);
			document.body.appendChild(link);
			link.click();
			if (link.parentNode) {
				link.parentNode.removeChild(link);
			}
			window.URL.revokeObjectURL(downloadUrl);
		} catch (error) {
			console.error("Ошибка при скачивании файла:", error);
		}
	};

	const renderFilterInputs = () => {
		return (
			<Formik
				initialValues={initialValues}
				enableReinitialize={true}
				onSubmit={() => {
					setPagination((prevValues: PaginationState) => {
						return { ...prevValues, pageIndex: 0 };
					});
					setIsFilterOpen(false);
					loadList();
				}}
				validateOnBlur={true}
				validateOnChange={true}
			>
				{(formik) => (
					<Form className="max-w-[1585px] w-full ">
						<div className="flex flex-row align-center justify-between">
							<p className="my-3 text-lg font-semibold">
								{t("documents_upload.filters")}
							</p>
							<button
								type="reset"
								onClick={() => {
									setIsFilterOpen(false);
								}}
							>
								<img src="/icons/close.svg" />
							</button>
						</div>
						<div className="flex flex-col">

							<div className={style.inputField}>
								<div className={style.labelAndButtonWrapper}>
									<label className={style.inputLabel}>ФИО</label>
								</div>
								<Field
									type="text"
									name="userFio"
									placeholder={t("documentLabels.choose_option")}
									className={style.placeholder}
									value={(formik as any)?.values?.userFio}
									onChange={(e: any) => {
										(formik as any).setFieldValue("userFio", e.target.value);
										setInitialValues((prevValues: any) => {
											return { ...prevValues, userFio: e.target.value };
										});
									}}
								/>
							</div>

							<div className={style.inputField}>
								<div className={style.labelAndButtonWrapper}>
									<label className={style.inputLabel}>ИИН/БИН организаций</label>
								</div>
								<Field
									type="text"
									name="orgIinBin"
									placeholder={t("documentLabels.choose_option")}
									className={style.placeholder}
									value={(formik as any)?.values?.orgIinBin}
									onChange={(e: any) => {
										(formik as any).setFieldValue("orgIinBin", e.target.value);
										setInitialValues((prevValues: any) => {
											return { ...prevValues, orgIinBin: e.target.value };
										});
									}}
								/>
							</div>

							<div className={style.inputField}>
								<div className={style.labelAndButtonWrapper}>
									<label className={style.inputLabel}>ID объекта</label>
								</div>
								<Field
									type="text"
									name="objectId"
									placeholder={t("documentLabels.choose_option")}
									className={style.placeholder}
									value={(formik as any)?.values?.objectId}
									onChange={(e: any) => {
										(formik as any).setFieldValue("objectId", e.target.value);
										setInitialValues((prevValues: any) => {
											return { ...prevValues, objectId: e.target.value };
										});
									}}
								/>
							</div>

							<div className={style.inputField}>
								<div className={style.labelAndButtonWrapper}>
									<label className={style.inputLabel}>Тип события</label>
								</div>
								<Field
								    component={SelectCustomFormik}
									name="eventType"
									placeholder={t("documentLabels.choose_option")}
									isMulti={true}
									options={eventTypeOptions}
									onChange={(selected: string[]) => {
										setInitialValues((prevValues) => ({
											...prevValues,
											eventType: selected ?? [],
										}));
									}}
								/>
							</div>

							<div className={style.inputField}>
								<div className={style.labelAndButtonWrapper}>
									<label className={style.inputLabel}>Описание события</label>
								</div>
								<Field
									component={SelectCustomFormik}
									name="description"
									placeholder={t("documentLabels.choose_option")}
									isMulti={true}
									onChange={(selected: string[]) => {
										setInitialValues((prevValues) => ({
											...prevValues,
											description: selected ?? [],
										}));
									}}
									options={actionOptions}
								/>
							</div>

							<div className={style.inputField}>
								<div className={style.labelAndButtonWrapper}>
									<label className={style.inputLabel}>Версия ПО</label>
								</div>
								<Field
									component={SelectCustomFormik}
									name="clientApp"
									placeholder={t("documentLabels.choose_option")}
									onChange={(selected: string) => {
										setInitialValues((prevValues) => ({
											...prevValues,
											clientApp: selected ?? null,
										}));
									}}
									options={[
										{ value: "web", label: "Веб-версия" },
										{ value: "mobile", label: "Мобильное приложение" }
									]}
								/>
							</div>

							<div className={style.inputField}>
								<div className={style.labelAndButtonWrapper}>
									<label className={style.inputLabel}>Дата события с / по</label>
								</div>
								<Calendar
									startDate={startDate}
									endDate={endDate}
									setStartDate={setStartDate}
									setEndDate={setEndDate}
								/>
							</div>
						</div>
						<div className="flex flex-row gap-[10px] justify-end mt-2">
							<button
								type="submit"
								className="bg-green text-white font-semibold py-2 px-4 rounded"
							>
								{t("mainButtons3.apply")}
							</button>
							<button
								type="reset"
								className="bg-bg text-gray-500 font-semibold py-2 px-4 rounded"
								onClick={() => {
									setStartDate(undefined);
									setEndDate(undefined);
									setInitialValues({
										userFio: "",
										orgIinBin: "",
										objectId: "",
										eventType: [],
										description: [],
										clientApp: null,
										createdAtFrom: "",
										createdAtTo: "",
									});
									(formik as any).resetForm({
										values: {
											userFio: "",
											orgIinBin: "",
											objectId: "",
											eventType: [],
											description: [],
											clientApp: null,
											createdAtFrom: "",
											createdAtTo: "",
										},
									});
									setPagination((prevValues: PaginationState) => {
										return { ...prevValues, pageIndex: 0 };
									});
								}}
							>
								{t("mainButtons3.reset")}
							</button>
						</div>
					</Form>
				)}
			</Formik>
		);
	};

	return (
		<>
			<div className="flex flex-1 flex-col min-w-0">
				<h2 className="my-3 text-xl font-semibold">Мониторинг действий пользователей</h2>
				<div className="bg-white my-5 p-5 pt-8 relative rounded-xl shadow-md flex flex-row items-center justify-between">
					<div className="flex-grow" />
					<div className="flex flex-row gap-[10px]">
						<button
							type="button"
							onClick={handleDownload}
							className="bg-green text-white font-semibold py-2 px-4 rounded"
						>
							{t("mainButtons.download")}
						</button>
						<button
							type="button"
							onClick={(e) => {
								e.stopPropagation();
								setIsFilterOpen(true);
							}}
							className="bg-green text-white font-semibold py-2 px-4 rounded"
							data-tooltip-offset={4}
							data-tooltip-id="filter-type"
						>
							{t("mainButtons3.filter")}
						</button>
						<button
							type="button"
							onClick={() => {
								loadList();
							}}
						>
							<img src="/icons/update.svg" alt="update" />
						</button>
					</div>
				</div>
				<div className="w-full">
					<div className="rounded-lg overflow-x-auto bg-white">
						<table className="max-w-full w-full leading-normal">
							<thead className="border-b-2 border-gray-300">
								<tr>
									<th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
										№
									</th>
									<th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
										ИИН/БИН организаций
									</th>
									<th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
										Пользователь
									</th>
									<th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
										Тип события
									</th>
									<th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
										Описание события
									</th>
									<th className="flex flex-row items-center justify-center px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
										Дата/время
										<button className="h-6 w-6 ml-2" onClick={toggleSort}>
											<img
												src={`/icons/arrow${sort === "ASC" ? "Down2" : "-top"}.svg`}
												className={`block h-6 w-6 ${sort === "ASC" ? "text-red-500" : ""}`}
												alt={sort === "ASC" ? "Ascending" : "Descending"}
											/>
										</button>
									</th>
									<th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
										Операционная система
									</th>
									<th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
										ID-объекта
									</th>
									<th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
										Версия ПО
									</th>
								</tr>
							</thead>
							
							{values.length === 0 ? (
								<tbody>
									<tr>
										<td colSpan={8} className="text-center py-5">
											<div>
												<EmptyListPlaceholderContent title="Данные отсутствуют" />
											</div>
										</td>
									</tr>
								</tbody>
							) : (
								<tbody className="font-normal text-center">
									{values.map((value: any, index: number) => (
										<tr key={`tr-${index}-${value?.id || index}`} className="">
											<td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
												{calculateItemNumber(index)}
											</td>
											<td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
												{value?.orgIinBin ?? "-"}
											</td>
											<td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
												{value?.userFio ?? "-"}
											</td>
											<td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
												{getEventTypeLabel(value?.eventType)}
											</td>
											<td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
												{getActionLabel(value?.action)}
											</td>
											<td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
												{formatCreatedAt(value?.createdAt)}
											</td>
											<td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
												{value?.operatingSystem ?? "-"}
											</td>
											<td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
												{value?.objectId ?? "-"}
											</td>
											<td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
												{value?.clientApp ?? "-"}
											</td>
										</tr>
									))}
								</tbody>
							)}
						</table>
					</div>
					<Pagination
						totalItems={tableData?.totalItems || 0}
						totalPages={tableData?.totalPages || 0}
						pagination={pagination}
						setPagination={setPagination}
					/>
				</div>
				<div className="z-[1000] relative">
					<Tooltip
						isOpen={isFilterOpen}
						id="filter-type"
						className={classNames(style.wrapperTooltip, "drop-shadow-2xl")}
						openOnClick={true}
						clickable={true}
						noArrow={true}
						opacity={1}
						place={"bottom-end"}
						afterShow={() => setIsFilterOpen(true)}
						afterHide={() => setIsFilterOpen(false)}
					>
						<div onClick={(e) => e.stopPropagation()}>{renderFilterInputs()}</div>
					</Tooltip>
				</div>
			</div>
		</>
	);
}

export default EventsAuditList;

